// Cartes produit (composant Figma « Collection Modal ») et carrousels. Porté de src/sections/shelf.js du
// prototype. Au survol, le détouré se transforme en photo lifestyle « en mosaïque du centre vers
// l'extérieur » (WebGL) ; pas de photo lifestyle = pas de survol. Le « + » ajoute au panier Shopify.
import { arrowCursor } from '@oqp/arrow-cursor';
import { createDissolve, drawCover, drawContain, pixelImage } from '@oqp/dissolve';

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const canHover = matchMedia('(hover: hover) and (min-width: 761px)');

// les navigateurs limitent le nombre de contextes WebGL (~16, le livret en prend un) :
// on ne garde que les calques des dernières cartes survolées
const POOL = 4;
const live = [];
function claim(entry) {
  const i = live.indexOf(entry);
  if (i >= 0) live.splice(i, 1);
  live.push(entry);
  while (live.length > POOL) live.shift().release();
}

const loaded = img => img.complete && img.naturalWidth ? Promise.resolve(img) : new Promise(r => { img.loading = 'eager'; img.addEventListener('load', () => r(img), { once: true }); });

const cards = new WeakMap();   // li → { enter, leave }

export function initCard(li) {
  if (cards.has(li)) return;
  cards.set(li, { enter() {}, leave() {} });
  // pas de photo lifestyle (ou carte photo seule) → pas d'état survolé
  if (!li.querySelector('.product__photo') || !li.querySelector('.product__shot')) return;
  const card = li.querySelector('.product__card');
  const shot = li.querySelector('.product__shot');
  const photo = li.querySelector('.product__photo');
  let dissolve = null, pending = null;

  async function setup() {
    if (dissolve || pending) return pending;
    pending = (async () => {
      try {
        await Promise.all([loaded(shot), loaded(photo)]);
        const [shotPx, photoPx] = await Promise.all([pixelImage(shot), pixelImage(photo)]);
        const canvas = document.createElement('canvas');
        canvas.className = 'product__canvas';
        canvas.setAttribute('aria-hidden', 'true');
        card.insertBefore(canvas, card.querySelector('.product__info'));
        const cw = card.clientWidth;
        dissolve = createDissolve(canvas,
          (ctx, w, h) => {                                   // repos : fond blanc + détouré à sa place Figma
            const s = w / cw;
            ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
            drawContain(ctx, shotPx, shot.offsetLeft * s, shot.offsetTop * s, shot.offsetWidth * s, shot.offsetHeight * s);
          },
          (ctx, w, h) => drawCover(ctx, photoPx, 0, 0, w, h),  // survol : photo lifestyle
          { duration: reduced ? 1 : 450, style: 'mosaic' });
        if (dissolve) li.classList.add('has-gl'); else canvas.remove();
      } catch (e) {
        dissolve = null;   // image inaccessible (CORS…) : on retombe sur le fondu CSS
      }
      pending = null;
    })();
    return pending;
  }
  const entry = {
    release() {
      if (!dissolve) return;
      dissolve.destroy(); dissolve = null;
      card.querySelector('.product__canvas')?.remove();
      li.classList.remove('has-gl');
    },
  };

  const enter = async () => {
    const carousel = li.closest('.carousel');   // null dans la grille de La collection
    if (carousel?.classList.contains('is-sliding') || li.getBoundingClientRect().right > document.documentElement.clientWidth + 1) return;
    if (li.classList.contains('is-hover')) return;
    li.classList.add('is-hover');
    claim(entry);
    await setup();
    if (li.classList.contains('is-hover')) dissolve && dissolve.to(1);
  };
  const leave = () => { li.classList.remove('is-hover'); dissolve && dissolve.to(0); };
  li.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse' && canHover.matches) enter(); });
  li.addEventListener('pointerleave', leave);
  li.addEventListener('focusin', enter);
  li.addEventListener('focusout', e => { if (!li.contains(e.relatedTarget)) leave(); });
  cards.set(li, { enter, leave });
}

// Carrousel : clic sur la carte coupée par le bord droit (curseur Arrow) → glisse d'une carte, en boucle
export function initCarousel(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = 'true';
  const track = root.querySelector('.carousel__track');
  let busy = false, px = -1, py = -1;
  root.addEventListener('pointermove', e => { px = e.clientX; py = e.clientY; });

  const isCut = e => {
    const li = e.target.closest('.product');
    return !!li && li.getBoundingClientRect().right > document.documentElement.clientWidth + 1;
  };

  function next() {
    if (busy || !canHover.matches) return;
    busy = true;
    root.classList.add('is-sliding');
    track.querySelectorAll('.product.is-hover').forEach(li => cards.get(li)?.leave());
    const first = track.firstElementChild;
    const step = first.getBoundingClientRect().width - 2;
    track.classList.add('is-sliding');
    track.style.transform = `translateX(${-step}px)`;
    const done = () => {
      track.classList.remove('is-sliding');
      track.appendChild(first);                   // la première carte repasse à la fin → sans fin
      track.style.transform = '';
      busy = false;
      root.classList.remove('is-sliding');
      const under = px >= 0 && document.elementFromPoint(px, py);
      const li = under && under.closest && under.closest('.product');
      if (li && root.contains(li)) cards.get(li)?.enter();
    };
    if (reduced) return done();
    const end = e => { if (e.target !== track || e.propertyName !== 'transform') return; track.removeEventListener('transitionend', end); done(); };
    track.addEventListener('transitionend', end);
  }

  root.addEventListener('click', e => {
    if (!canHover.matches || !isCut(e)) return;
    e.preventDefault();
    e.stopPropagation();
    next();
  }, true);
  root.querySelector('.carousel__next')?.addEventListener('click', next);
  arrowCursor(root, e => canHover.matches && isCut(e));

  // assez de cartes pour toujours remplir la largeur + une qui arrive : on clone la liste une fois
  if (canHover.matches) {
    [...track.children].forEach(li => {
      const c = li.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.querySelectorAll('a, button').forEach(el => el.tabIndex = -1);
      track.appendChild(c);
    });
  }
  track.querySelectorAll('.product').forEach(initCard);
}

// La collection : mêmes cartes, en grille
export function initProductGrid(root) {
  root.querySelectorAll('.product').forEach(initCard);
}

// « + » des cartes : ajout au panier par l'action standard Shopify (le tiroir Horizon s'ouvre et se met à jour)
async function addToCart(btn) {
  if (btn.disabled) return;
  const line = { merchandiseId: btn.dataset.variantId, quantity: 1 };
  if (btn.dataset.sellingPlan) line.sellingPlanId = btn.dataset.sellingPlan;   // abonnement : plan Appstle obligatoire
  btn.disabled = true;
  try {
    if (window.Shopify?.actions?.updateCart) {
      await window.Shopify.actions.updateCart({ lines: [line] });
    } else {
      const body = { items: [{ id: Number(line.merchandiseId), quantity: 1, ...(line.sellingPlanId && { selling_plan: Number(line.sellingPlanId) }) }] };
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    }
  } finally {
    btn.disabled = false;
  }
}
document.addEventListener('click', e => {
  const btn = e.target.closest('.product__add[data-variant-id]');
  if (!btn) return;
  e.preventDefault();
  addToCart(btn);
});

function initAll(scope = document) {
  scope.querySelectorAll('[data-carousel]').forEach(initCarousel);
  scope.querySelectorAll('[data-product-grid]').forEach(initProductGrid);
}
initAll();
document.addEventListener('shopify:section:load', e => initAll(e.target));
